/**
 * Stacked-segment helpers shared by the monthly and daily Sales History charts.
 *
 * Generalises the Sales Analysis source-mix convention to two breakdown
 * dimensions — sales channel and child SKU — without duplicating its palette.
 * Colours come from `getSourceColor`, so a channel keeps the colour it has in
 * Sales Analysis and a child SKU is coloured from the same categorical ramp.
 *
 * A chart stacks by exactly one dimension at a time. SKU x channel would be an
 * N x M colour matrix nobody can read.
 */

import {
  getSourceColor,
  sourceSalesDataKey,
} from "../../components/metrics/sourceSalesMix";

export const CHANNEL_BREAKDOWN = {
  key: "source_breakdown",
  field: "source",
  // Used in the "grouped as Other" note so it names the dimension actually
  // being folded, rather than always saying "channels".
  noun: "channels",
};

export const SKU_BREAKDOWN = {
  key: "sku_breakdown",
  field: "sku",
  noun: "SKUs",
};

// Beyond this the legend stops being readable, so the long tail folds into one
// "Other" segment.
export const MAX_LEGEND_SEGMENTS = 10;
export const OTHER_LABEL = "Other";
export const OTHER_COLOR = "#94A3B8";

export const segmentDataKey = sourceSalesDataKey;

export const segmentColor = (name, options = []) =>
  name === OTHER_LABEL ? OTHER_COLOR : getSourceColor(name, options);

/**
 * Names that actually contributed, ordered by `preferredOrder` (the channel
 * filter's option list, or the product's child-SKU list) so colour and stack
 * order stay stable as the data changes.
 */
export const collectSegmentNames = (rows = [], dimension, preferredOrder = []) => {
  const present = new Set();
  (rows || []).forEach((row) => {
    (row?.[dimension.key] || []).forEach((part) => {
      const name = part?.[dimension.field];
      if (name && part.units_sold > 0) present.add(name);
    });
  });
  const ordered = preferredOrder.filter((name) => present.has(name));
  const extras = [...present]
    .filter((name) => !preferredOrder.includes(name))
    .sort((a, b) => a.localeCompare(b));
  return [...ordered, ...extras];
};

/** Rank by total units so the long tail folded into "Other" is the smallest. */
export const limitSegmentNames = (names, rows = [], dimension) => {
  if (names.length <= MAX_LEGEND_SEGMENTS) return names;
  const totals = new Map();
  (rows || []).forEach((row) => {
    (row?.[dimension.key] || []).forEach((part) => {
      const name = part?.[dimension.field];
      if (!name) return;
      totals.set(name, (totals.get(name) || 0) + (part.units_sold || 0));
    });
  });
  const ranked = [...names].sort(
    (a, b) => (totals.get(b) || 0) - (totals.get(a) || 0),
  );
  return [...ranked.slice(0, MAX_LEGEND_SEGMENTS), OTHER_LABEL];
};

/**
 * The list a chart should take its colours from.
 *
 * Colour index comes from position in THIS list, not in the client's full
 * channel / child-SKU list. That matters because `getSourceColor` does
 * `index % palette.length`: indexing into a 19-channel or 24-SKU list pushes
 * segments onto arbitrary hues and, past index 14, wraps them onto exact
 * duplicates. A chart draws at most MAX_LEGEND_SEGMENTS + "Other", so indexing
 * densely always lands on the maximally-separated head of the ramp
 * (blue, red, green, orange, purple, …) and can never wrap.
 *
 * Order still follows `preferredOrder`, so the assignment stays deterministic.
 */
export const resolveSegmentOrder = (rows, dimension, preferredOrder = []) =>
  limitSegmentNames(
    collectSegmentNames(rows, dimension, preferredOrder),
    rows,
    dimension,
  );

/**
 * Widen each row with one numeric key per stacked segment.
 *
 * `isBlank` marks rows with no evidence; those get null on every key so the
 * bar stays absent rather than stacking to zero.
 */
export const buildStackedRows = ({
  rows = [],
  dimension,
  names = [],
  options = [],
  isBlank = () => false,
}) => {
  const named = new Set(names.filter((name) => name !== OTHER_LABEL));
  return rows.map((row) => {
    const next = { ...row };
    if (isBlank(row)) {
      names.forEach((name) => {
        next[segmentDataKey(name)] = null;
      });
      next.segments = [];
      return next;
    }
    const totals = new Map();
    (row?.[dimension.key] || []).forEach((part) => {
      const name = part?.[dimension.field];
      if (!name || !(part.units_sold > 0)) return;
      const bucket = named.has(name) ? name : OTHER_LABEL;
      totals.set(bucket, (totals.get(bucket) || 0) + part.units_sold);
    });
    names.forEach((name) => {
      next[segmentDataKey(name)] = totals.get(name) || 0;
    });
    next.segments = names
      .filter((name) => totals.get(name) > 0)
      .map((name) => ({
        name,
        units_sold: totals.get(name),
        color: segmentColor(name, options),
      }));
    return next;
  });
};
